export interface SoundSettings {
  enabled: boolean;
  volume: number; // 0-1
  soundType: 'default' | 'chime' | 'bell' | 'pop' | 'ding';
}

class SoundService {
  private audioContext: AudioContext | null = null;
  private sounds: Map<string, AudioBuffer> = new Map();
  private fallbackAudio: HTMLAudioElement | null = null;
  private userInteracted: boolean = false;
  private settings: SoundSettings = {
    enabled: true,
    volume: 0.7,
    soundType: 'default'
  };

  constructor() {
    this.loadSettings();
    this.initializeAudioContext();
    this.initializeFallbackAudio();
    this.setupUserInteractionListener();
  }

  private initializeAudioContext() {
    try {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      console.log('🔊 Web Audio API initialized successfully');
    } catch (error) {
      console.warn('🔇 Web Audio API not supported:', error);
    }
  }

  private initializeFallbackAudio() {
    try {
      // Create a simple beep sound data URL
      const beepDataUrl = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OScTgwOUarm7bJlHgU2jdXzzn0vBSF1xe/eizEIHWq+8+OWT';
      this.fallbackAudio = new Audio(beepDataUrl);
      this.fallbackAudio.volume = this.settings.volume;
      console.log('🔊 Fallback audio initialized');
    } catch (error) {
      console.warn('🔇 Failed to initialize fallback audio:', error);
    }
  }

  private setupUserInteractionListener() {
    const handleUserInteraction = () => {
      this.userInteracted = true;
      console.log('🔊 User interaction detected, audio unlocked');
      
      // Try to resume audio context if suspended
      if (this.audioContext && this.audioContext.state === 'suspended') {
        this.audioContext.resume().then(() => {
          console.log('🔊 Audio context resumed after user interaction');
        });
      }
      
      // Remove listeners after first interaction
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
    };

    document.addEventListener('click', handleUserInteraction);
    document.addEventListener('keydown', handleUserInteraction);
    document.addEventListener('touchstart', handleUserInteraction);
  }

  private loadSettings() {
    try {
      const saved = localStorage.getItem('notification-sound-settings');
      if (saved) {
        this.settings = { ...this.settings, ...JSON.parse(saved) };
      }
    } catch (error) {
      console.warn('Failed to load sound settings:', error);
    }
  }

  private saveSettings() {
    try {
      localStorage.setItem('notification-sound-settings', JSON.stringify(this.settings));
    } catch (error) {
      console.warn('Failed to save sound settings:', error);
    }
  }

  // Generate different notification sounds using Web Audio API
  private generateSound(type: string): AudioBuffer | null {
    if (!this.audioContext) return null;

    const sampleRate = this.audioContext.sampleRate;
    const duration = 0.3; // 300ms
    const length = sampleRate * duration;
    const buffer = this.audioContext.createBuffer(1, length, sampleRate);
    const data = buffer.getChannelData(0);

    switch (type) {
      case 'default':
        // Simple notification beep
        for (let i = 0; i < length; i++) {
          const t = i / sampleRate;
          data[i] = Math.sin(2 * Math.PI * 800 * t) * Math.exp(-t * 3) * 0.3;
        }
        break;

      case 'chime':
        // Pleasant chime sound
        for (let i = 0; i < length; i++) {
          const t = i / sampleRate;
          data[i] = (
            Math.sin(2 * Math.PI * 523.25 * t) * Math.exp(-t * 2) * 0.2 + // C5
            Math.sin(2 * Math.PI * 659.25 * t) * Math.exp(-t * 2) * 0.2 + // E5
            Math.sin(2 * Math.PI * 783.99 * t) * Math.exp(-t * 2) * 0.2   // G5
          ) / 3;
        }
        break;

      case 'bell':
        // Bell-like sound
        for (let i = 0; i < length; i++) {
          const t = i / sampleRate;
          data[i] = Math.sin(2 * Math.PI * 1000 * t) * Math.exp(-t * 4) * 0.4;
        }
        break;

      case 'pop':
        // Quick pop sound
        for (let i = 0; i < length * 0.1; i++) {
          const t = i / sampleRate;
          data[i] = Math.sin(2 * Math.PI * 1200 * t) * Math.exp(-t * 20) * 0.5;
        }
        break;

      case 'ding':
        // Ding sound
        for (let i = 0; i < length; i++) {
          const t = i / sampleRate;
          data[i] = Math.sin(2 * Math.PI * 1500 * t) * Math.exp(-t * 5) * 0.3;
        }
        break;

      default:
        return this.generateSound('default');
    }

    return buffer;
  }

  private async loadSound(type: string): Promise<AudioBuffer | null> {
    if (this.sounds.has(type)) {
      return this.sounds.get(type) || null;
    }

    const buffer = this.generateSound(type);
    if (buffer) {
      this.sounds.set(type, buffer);
    }
    return buffer;
  }

  async playNotificationSound(type?: string): Promise<void> {
    console.log('🔊 Attempting to play notification sound:', {
      enabled: this.settings.enabled,
      hasAudioContext: !!this.audioContext,
      audioContextState: this.audioContext?.state,
      userInteracted: this.userInteracted,
      volume: this.settings.volume,
      soundType: type || this.settings.soundType
    });

    if (!this.settings.enabled) {
      console.log('🔇 Sound disabled in settings');
      return;
    }

    if (!this.userInteracted) {
      console.log('🔇 No user interaction yet, cannot play audio');
      return;
    }

    if (!this.audioContext) {
      console.warn('🔇 No audio context available, trying fallback');
      this.playFallbackSound();
      return;
    }

    try {
      // Resume audio context if suspended (required by some browsers)
      if (this.audioContext.state === 'suspended') {
        console.log('🔊 Resuming suspended audio context');
        await this.audioContext.resume();
      }

      const soundType = type || this.settings.soundType;
      console.log('🔊 Loading sound type:', soundType);
      const buffer = await this.loadSound(soundType);
      
      if (!buffer) {
        console.warn('🔇 Failed to load sound buffer:', soundType);
        return;
      }

      const source = this.audioContext.createBufferSource();
      const gainNode = this.audioContext.createGain();
      
      source.buffer = buffer;
      gainNode.gain.value = this.settings.volume;
      
      source.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      
      console.log('🔊 Playing sound with volume:', this.settings.volume);
      source.start();
      
      // Log when sound finishes
      source.onended = () => {
        console.log('🔊 Sound playback finished');
      };
      
    } catch (error) {
      console.error('🔇 Failed to play notification sound with Web Audio API:', error);
      console.log('🔊 Attempting fallback audio method');
      this.playFallbackSound();
    }
  }

  private playFallbackSound(): void {
    try {
      if (this.fallbackAudio) {
        this.fallbackAudio.volume = this.settings.volume;
        this.fallbackAudio.currentTime = 0;
        const playPromise = this.fallbackAudio.play();
        
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              console.log('🔊 Fallback sound played successfully');
            })
            .catch((error) => {
              console.error('🔇 Fallback sound failed:', error);
            });
        }
      } else {
        console.warn('🔇 No fallback audio available');
      }
    } catch (error) {
      console.error('🔇 Fallback sound error:', error);
    }
  }

  updateSettings(newSettings: Partial<SoundSettings>) {
    this.settings = { ...this.settings, ...newSettings };
    this.saveSettings();
    
    // Update fallback audio volume if it exists
    if (this.fallbackAudio && newSettings.volume !== undefined) {
      this.fallbackAudio.volume = newSettings.volume;
    }
  }

  getSettings(): SoundSettings {
    return { ...this.settings };
  }

  // Test sound for settings
  async testSound(type?: string): Promise<void> {
    const originalEnabled = this.settings.enabled;
    this.settings.enabled = true;
    
    // Force user interaction flag for testing
    this.userInteracted = true;
    
    await this.playNotificationSound(type);
    this.settings.enabled = originalEnabled;
  }

  // Check if audio is supported
  isSupported(): boolean {
    return !!this.audioContext;
  }
}

export const soundService = new SoundService();
